<template>
  <div class="data-table-container">
    <div class="level is-mobile" v-if="allowSearch">
      <div class="level-left">
        <div class="level-item">
          <strong>Search by</strong>
        </div>
        <div class="level-item">
          <span class="select">
            <select @change="setQueryColumn">
              <option :value="null">Everything</option>
              <option v-for="column in columns" v-if="column.searchable !== false"
                      :value="column.name"
                      :selected="queryColumn.name === column.name">{{ column.name }}</option>
            </select>
          </span>
        </div>
        <div class="level-item">
          <input type="text" class="input search-input" @input="setQuery($event.target.value)"
                 placeholder="Type to search..." :value="query">
        </div>
      </div>
    </div>

    <paginator :current_page="currentPage"
               :data_length="data.length"
               :page_length="fluidPageLength"
               @set-page="setPage"
               @set-page-length="setPageLength"
               v-if="showPagination"></paginator>

    <table class="table is-fullwidth is-hoverable is-striped data-table">
      <thead>
      <tr>
        <th class="data-table-header" v-for="column in columns" v-if="column.display !== false"
            @click="applySort(column)">
          {{ column.name }}
          <span v-if="column.sortable !== false" class="icon">
            <i class="fa fa-sort" v-if="sort !== column"></i>
            <i class="fa fa-sort-alpha-asc"
               v-if="sort === column && column.type === 'text' && sortDirection === 'ASC'"></i>
            <i class="fa fa-sort-alpha-desc"
               v-if="sort === column && column.type === 'text' && sortDirection === 'DESC'"></i>
            <i class="fa fa-sort-numeric-asc"
               v-if="sort === column && column.type === 'number' && sortDirection === 'ASC'"></i>
            <i class="fa fa-sort-numeric-desc"
               v-if="sort === column && column.type === 'number' && sortDirection === 'DESC'"></i>
            <i class="fa fa-sort-amount-asc"
               v-if="sort === column && column.type !== 'text' && column.type !== 'number' && sortDirection === 'ASC'"></i>
            <i class="fa fa-sort-amount-desc"
               v-if="sort === column && column.type !== 'text' && column.type !== 'number' && sortDirection === 'DESC'"></i>
          </span>
        </th>
        <th v-if="$scopedSlots.controls">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(row, index) in data"
          v-if="index >= (fluidPageLength * (currentPage-1)) && index < (fluidPageLength * currentPage)">
        <td v-for="column in columns" v-if="column.display !== false">
          <slot v-if="$scopedSlots['column_'+column.name]" :name="'column_'+column.name" :row="row"></slot>
          <span v-else v-html="getColumnDisplay(row, column)"
                :title="getColumnData(row, column).join(' ')"></span>
        </td>
        <slot name="controls" :row="row"></slot>
      </tr>
      </tbody>
    </table>


    <paginator :current_page="currentPage"
               :data_length="data.length"
               :page_length="fluidPageLength"
               @set-page="setPage"
               @set-page-length="setPageLength"
               v-if="showPagination"></paginator>
  </div>
</template>

<style lang="scss" rel="stylesheet/scss">
.page-select {
  &:hover {
    cursor: pointer;
  }
}

.active-page {
  font-weight: bold;
}

.search-toggle {
  &:hover {
    cursor: pointer;
  }
}

.search-input {
  display: inline-block;
  width: 200px;
  transition: width 0.5s ease;

  &:focus {
    width: 400px;
  }
}

.data-table {
  min-width: 600px;
}

.data-table-header {
  &:hover {
    user-select: none;
    cursor: pointer;
  }
}

.data-images {
  display: inline-block;
  max-width: 30px;
}

.data-image {
  max-height: 30px;
}

</style>

<script>
import { mapGetters } from "vuex"
import Vue from "vue"
import FuzzySearch from "../utils/FuzzySearch.js"
import Paginator from "./misc/Paginator";

export default {
  components: { Paginator },
  props: {
    dataSet: { type: [Object, Array] },
    columns: { type: [Object, Array] },
    pageLength: { type: [Number], default: 10 },
    allowSearch: { type: [Boolean], default: true },
  },
  data() {
    return {
      currentPage: Math.max(this.$route.query.page ? parseInt(this.$route.query.page) : 1, 1),
      setQueryTimer: 0,
      setQueryDebounce: 500,
      query: '',
      queryColumn: '',
      sort: false,
      sortDirection: 'DESC',
      fluidPageLength: this.$route.query.perPage ? parseInt(this.$route.query.perPage) : 10,
    }
  },
  beforeMount() {
    this.fluidPageLength = this.$route.query.perPage ? parseInt(this.$route.query.perPage) : this.pageLength;

    if (this.$route.query.sort) {
      let columns = JSON.parse(JSON.stringify(this.columns))
      columns.push({ name: 'Score', prop: '__result_score', type: 'number' })
      let sort    = decodeURIComponent(this.$route.query.sort)
      let column  = columns.find(column => column.name === sort)
      let sortDir = (this.$route.query.dir) ? this.$route.query.dir : 'ASC'
      this.applySortNoUpdate(column)
      this.sortDirection = sortDir
    }
    if (this.$route.query.query) {
      this.query = decodeURIComponent(this.$route.query.query)
      if (this.$route.query.column) {
        this.queryColumn = this.columns.find(column => column.name === decodeURIComponent(this.$route.query.column))
      }
    }
  },
  computed: Object.assign({}, mapGetters(['images']), {
    data() {
      let data = JSON.parse(JSON.stringify(this.dataSet))

      let minScore = 2;

      if (this.query.length > 0) {
        let pool = []
        for (let row in data) {
          let contestant = []
          if (this.queryColumn.length === 0) {
            for (let column in this.columns) {
              let columnDetails = this.columns[column];
              if (columnDetails.searchable !== false && columnDetails.nested !== true) {
                contestant.push(this.getColumnData(data[row], columnDetails).join(' '))
              }
            }
          }
          else {
            if (this.queryColumn.nested === true) {
              minScore = 7;
              if (typeof data[row][this.queryColumn.prop] !== 'undefined') {
                data[row][this.queryColumn.prop].forEach(i => {
                  contestant.push(this.getColumnData(i, { prop: this.queryColumn.subprop }).join(' '))
                });
              }
              else {
                console.log('wa')
              }
            }
            else {
              contestant.push(this.getColumnData(data[row], this.queryColumn).join(' '))
            }
          }
          if (contestant.length > 0) {
            pool.push(contestant)
          }
        }

        let scores = []
        for (let contestant in pool) {
          let haystack     = pool[contestant]
          let needleFinder = new FuzzySearch({
            source: haystack,
            output_map: "root",
            score_round: 0.01,
            minimum_match: 0.75,
            token_query_min_length: 1,
            token_field_min_length: 1,
            thresh_include: minScore,
          })
          let result       = needleFinder.search(this.query);
          if (result[0]) {
            scores.push({
              row: contestant,
              score: result[0].score,
            })
          }
        }

        // assign data rows to row index from scores - add __result_score property to row
        let results = [];
        for (let score in scores) {
          results.push(Object.assign({}, data[parseInt(scores[score].row)], { __result_score: scores[score].score }))
        }

        data = results
      }

      if (this.sort !== false) {
        if (this.sort.type === 'text') {
          data.sort(this.sortLocaleCompare)
        }
        else if (this.sort.type === 'boolean') {
          data.sort(this.sortBoolean)
        }
        else if (this.sort.type === 'date') {
          data.sort(this.sortDate)
        }
        else if (this.sort.type === 'number') {
          data.sort((a, b) => {
            return this.getColumnData(a, this.sort).join() - this.getColumnData(b, this.sort).join()
          })
        }
        else {
          data.sort()
        }

        if (this.sortDirection === 'DESC') {
          data.reverse()
        }
      }

      return data
    },
    showPagination() {
      return true;
      //return Math.ceil(this.data.length / this.fluidPageLength) > 1
    },
  }),
  methods: {

    updateRoute() {
      let query = {
        page: this.currentPage,
        perPage: this.fluidPageLength,
      }
      if (this.sort !== false) {
        query.sort = this.sort.name
        query.dir  = this.sortDirection
      }
      if (this.query.length > 0 || this.query !== '') {
        query.query  = this.query
        query.column = this.queryColumn.name
      }
      this.$router.push({ name: this.$route.name, params: this.$route.params, query })
    },

    setPage(number) {
      this.currentPage = Math.max(number, 1);
      this.checkMaxPage()
      this.updateRoute()
    },

    setPageLength(length) {
      this.fluidPageLength = length
      this.checkMaxPage()
      this.updateRoute()
    },

    checkMaxPage() {
      if (this.currentPage > Math.ceil(this.data.length / this.fluidPageLength)) {
        this.currentPage = Math.max(Math.ceil(this.data.length / this.fluidPageLength), 1);
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.setPage(this.currentPage - 1)
      }
    },

    nextPage() {
      let pageCount = Math.round(this.data.length / this.fluidPageLength);
      if (this.currentPage < pageCount) {
        this.setPage(this.currentPage + 1)
      }
    },

    setQuery(value) {
      clearTimeout(this.setQueryTimer);
      this.setQueryTimer = setTimeout(() => {
        this.query = value
      }, this.setQueryDebounce)
    },

    getColumnData(row, column) {
      let columnData = []
      // get column data, array denotes multiple props make up this column, string denotes a single prop
      if (column.prop instanceof Array) {
        for (let propIndex in column.prop) {
          if (row.hasOwnProperty(column.prop[propIndex])) {
            columnData.push(row[column.prop[propIndex]])
          }
        }
      }
      else {
        if (row.hasOwnProperty(column.prop)) {
          columnData.push(row[column.prop])
        }
      }

      return columnData
    },

    getColumnDisplay(row, column) {
      let columnData = this.getColumnData(row, column).join(' ')

      // apply filter if defined
      if (column.filter) {
        if (column.filter instanceof Array) {
          if (column.filter[1].length === 2) {
            columnData = Vue.filter(column.filter[0])(columnData, column.filter[1][0], column.filter[1][1])
          }
          else {
            columnData = Vue.filter(column.filter[0])(columnData, column.filter[1][0])
          }
        }
        else {
          columnData = Vue.filter(column.filter)(columnData)
        }
      }

      return columnData
    },

    /**
     * SORTING
     */
    applySort(column) {
      if (column.sortable === false) return false

      if (this.sort.prop === column.prop) {
        if (this.sortDirection === 'DESC') {
          if (this.query.length > 0) {
            this.sort          = { name: 'Score', prop: '__result_score', type: 'number' }
            this.sortDirection = 'DESC'
          }
          else {
            this.sort = false
          }
          this.updateRoute()
          return
        }
        else {
          this.sortDirection = 'DESC'
          this.updateRoute()
          return
        }
      }

      this.sort          = column
      this.sortDirection = 'ASC'
      this.updateRoute()
    },

    applySortNoUpdate(column) {
      if (column.sortable === false) return false

      if (this.sort.prop === column.prop) {
        if (this.sortDirection === 'DESC') {
          if (this.query.length > 0) {
            this.sort          = { name: 'Score', prop: '__result_score', type: 'number' }
            this.sortDirection = 'DESC'
          }
          else {
            this.sort = false
          }
          return
        }
        else {
          this.sortDirection = 'DESC'
          return
        }
      }

      this.sort          = column
      this.sortDirection = 'ASC'
    },

    sortLocaleCompare(a, b) {
      a = this.getColumnData(a, this.sort).join(' ')
      b = this.getColumnData(b, this.sort).join(' ')
      return a.localeCompare(b);
    },

    sortBoolean(a, b) {
      a = this.getColumnData(a, this.sort)[0] === true ? 1 : 0;
      b = this.getColumnData(b, this.sort)[0] === true ? 1 : 0;
      return b - a;
    },

    sortAlphaNum(a, b) {
      a        = this.getColumnData(a, this.sort).join(' ')
      b        = this.getColumnData(b, this.sort).join(' ')
      let reA  = /[^a-zA-Z]/g;
      let reN  = /[^0-9]/g;
      let AInt = parseInt(a, 10);
      let BInt = parseInt(b, 10);

      if (isNaN(AInt) && isNaN(BInt)) {
        let aA = a.replace(reA, "");
        let bA = b.replace(reA, "");
        if (aA === bA) {
          let aN = parseInt(a.replace(reN, ""), 10);
          let bN = parseInt(b.replace(reN, ""), 10);
          return aN === bN ? 0 : aN > bN ? 1 : -1;
        }
        else {
          return aA > bA ? 1 : -1;
        }
      }
      else if (isNaN(AInt)) {//A is not an Int
        return 1;//to make alphanumeric sort first return -1 here
      }
      else if (isNaN(BInt)) {//B is not an Int
        return -1;//to make alphanumeric sort first return 1 here
      }
      else {
        return AInt > BInt ? -1 : 1;
      }
    },

    sortDate(a, b) {
      a      = this.getColumnData(a, this.sort).join(' ')
      b      = this.getColumnData(b, this.sort).join(' ')
      let aA = new Date(a).getTime();
      let bA = new Date(b).getTime();
      return aA === bA ? 0 : aA > bA ? -1 : 1
    },

    /**
     * SEARCHING
     */
    setQueryColumn(e) {
      if (e.target.value === null) {
        this.queryColumn = ''
      }
      else {
        this.queryColumn = this.columns.find(column => column.name === e.target.value) || ''
      }
      this.updateRoute()
    },

    onQueryUpdate(newQuery, oldQuery) {
      if (oldQuery.length === 0 && newQuery.length > 0) {
        this.sort          = { name: 'Score', prop: '__result_score', type: 'number' }
        this.sortDirection = 'DESC'
      }
      else if (newQuery.length === 0 && this.sortDirection === 'DESC') {
        this.sort          = false
        this.sortDirection = 'ASC'
      }
      this.updateRoute()
    },
    onRouteUpdate(newRoute, oldRoute) {
      if (Object.keys(newRoute).length === 0) {
        this.currentPage     = 1;
        this.fluidPageLength = this.pageLength;
      }
      else {
        if (newRoute.page) {
          this.currentPage = parseInt(newRoute.page);
        }
        if (newRoute.perPage) {
          this.fluidPageLength = parseInt(newRoute.perPage);
        }
        this.checkMaxPage();
      }
    },
  },
  watch: {
    query: 'onQueryUpdate',
    '$route.query': 'onRouteUpdate',
  },
};

function escapeHtml(unsafe) {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
</script>



// WEBPACK FOOTER //
// DataTable.vue?a116ab14